<script lang="ts" setup>
// ============================================================================
// DataRawReadingsTable (DEV-595, C1)
// ----------------------------------------------------------------------------
// Tableau des RELEVÉS BRUTS d'une source migrée dataV2 (cas PCE GRDF), vue
// « par période » : 1 ligne = 1 relevé publié [date_from → date_to], colonnes
// dans l'ordre de la chaîne de mesure (Index · Volume brut · Coeff · PCS ·
// Volume converti · Énergie). La qualité de chaque relevé (provenance ADICT)
// est portée par la COULEUR du badge, dérivée du code `t` (cf. la légende
// `meta.provenance` renvoyée par la route `POST /source/datav2/series`).
//
// 100 % données brutes GRDF : pas de coût (calculé), pas de métadonnée
// (contractuel/technique → page /info-general).
// ============================================================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { ColumnConfig } from "@types"
import DataQualityBadge from "../quality/DataQualityBadge.vue"
import type { ProvenanceCode } from "../quality/dataQuality"

export type { ProvenanceCode }

/** Un relevé brut (grain `periode`) prêt à afficher. */
export type RawReading = {
    /** clé unique de ligne (= `date_to`) */
    id: string
    /** bornes de la période publiée (ISO `YYYY-MM-DD`) */
    dateFrom: string
    dateTo: string
    /** index volume cumulé — relevé de fin (m³) */
    index: number | null
    /** volume brut (m³) */
    volumeBrut: number | null
    /** coefficient de conversion volume→énergie (kWh/m³) */
    coeff: number | null
    /** PCS (kWh/m³) — souvent null côté GRDF */
    pcs: number | null
    /** volume converti / corrigé (m³) */
    volumeConverti: number | null
    /** énergie (kWh) */
    energie: number | null
    /** code provenance/qualité (cf. ProvenanceCode) */
    t: ProvenanceCode
}

const props = defineProps<{ readings: RawReading[] }>()

const { t: tr } = useI18n({
    messages: {
        fr: {
            col: {
                periode: "Période",
                index: "Index volume",
                volumeBrut: "Volume brut",
                coeff: "Coeff. conversion",
                pcs: "PCS",
                volumeConverti: "Volume converti",
                energie: "Énergie",
                qualite: "Qualité",
            },
            empty: "Aucune donnée remontée sur la période sélectionnée.",
        },
    },
})

const columns = computed<ColumnConfig[]>(() => [
    { field: "periode", header: tr("col.periode"), sortable: true },
    { field: "index", header: tr("col.index") },
    { field: "volumeBrut", header: tr("col.volumeBrut") },
    { field: "coeff", header: tr("col.coeff") },
    { field: "pcs", header: tr("col.pcs") },
    { field: "volumeConverti", header: tr("col.volumeConverti") },
    { field: "energie", header: tr("col.energie") },
    { field: "qualite", header: tr("col.qualite") },
])

function fmt(v: number | null | undefined, digits = 2): string {
    if (v === null || v === undefined || Number.isNaN(v)) return "-"
    return new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: digits,
    }).format(v)
}
function fmtDate(d: string): string {
    const [y, m, day] = d.split("-")
    return `${day}/${m}/${y}`
}

// alias pour éviter le shadow de `t` (i18n) dans le template
const hasData = computed<boolean>(() => props.readings.length > 0)
</script>

<template>
    <BaseTable
        v-if="hasData"
        :columns="columns"
        :value="readings"
        data-key="id"
        :rows="12"
        :rows-per-page-options="[12, 24, 48]"
        scrollable
    >
        <template #cell_periode="{ data }">
            <span class="whitespace-nowrap">
                {{ fmtDate(data.dateFrom) }} → {{ fmtDate(data.dateTo) }}
            </span>
        </template>
        <template #cell_index="{ data }">{{ fmt(data.index, 0) }} m³</template>
        <template #cell_volumeBrut="{ data }"
            >{{ fmt(data.volumeBrut) }} m³</template
        >
        <template #cell_coeff="{ data }">{{ fmt(data.coeff, 4) }}</template>
        <template #cell_pcs="{ data }">
            {{ data.pcs === null ? "-" : fmt(data.pcs, 4) }}
        </template>
        <template #cell_volumeConverti="{ data }">
            {{ fmt(data.volumeConverti) }} m³
        </template>
        <template #cell_energie="{ data }"
            >{{ fmt(data.energie) }} kWh</template
        >
        <template #cell_qualite="{ data }">
            <DataQualityBadge :code="data.t" />
        </template>
    </BaseTable>
    <div v-else class="text-center text-slate-400 py-8">
        {{ tr("empty") }}
    </div>
</template>
